/**
 * TokenResolverService
 *
 * Centralizes resolution of special token values in filters.
 * Supports dynamic user context tokens and date tokens.
 *
 * Token Types:
 * - ME: Resolves to current user ID (instanceUserId)
 * - CURRENT_CUSTOMER: Resolves to current customer ID in ecommerce context
 * - MY_WAREHOUSE: Resolves to the user's active (default) warehouse
 * - TODAY: Resolves to current date
 * - NOW: Resolves to current datetime
 */

import type { WarehouseRefModel } from '@supply-mover-2-0/shared';

/**
 * Available token types for filter values.
 */
export type TokenType =
	| 'ME'
	| 'CURRENT_CUSTOMER'
	| 'MY_WAREHOUSE'
	| 'TODAY'
	| 'NOW';

/**
 * Context data required for token resolution.
 */
export interface TokenContext {
	/**
	 * Current user ID (instanceUserId in the system).
	 */
	userId?: number;

	/**
	 * Current customer ID for ecommerce context.
	 */
	customerId?: string;

	/**
	 * User display name for UI purposes.
	 */
	userDisplayName?: string;

	/**
	 * The caller's active (default) warehouse, used to resolve the MY_WAREHOUSE
	 * filter token to a concrete warehouse so the list chip shows the real name
	 * (PD-13742). Null/absent when the user has no default — the token then
	 * resolves to nothing and the filter falls back to "all warehouses".
	 */
	activeWarehouse?: WarehouseRefModel | null;

	/**
	 * Reference date for date token resolution.
	 * Defaults to current date if not provided.
	 */
	referenceDate?: Date;
}

/**
 * Result of resolving a token value.
 */
export interface ResolvedToken {
	/**
	 * The resolved value (ID string or date string).
	 */
	id: string;

	/**
	 * Display label for the resolved value.
	 */
	label: string;
}

/**
 * Options for {@link TokenResolverService.resolveFilters}.
 */
export interface ResolveFiltersOptionsModel {
	/**
	 * Tokens to keep as-is instead of resolving (still de-duplicated like any
	 * other value). The list builder keeps ME so filter state — and therefore
	 * a saved or shared list — keeps meaning "whoever is looking" (PD-15272).
	 */
	preserveTokens?: ReadonlyArray<TokenType>;
}

/**
 * Service for resolving token values in filters.
 */
export class TokenResolverService {
	/**
	 * Resolve a token to its actual value.
	 *
	 * @param token The token to resolve (ME, CURRENT_CUSTOMER, etc.)
	 * @param context The context containing user/customer data
	 * @returns Resolved token with id and label, or null if cannot resolve
	 */
	static resolveToken(
		token: TokenType,
		context: TokenContext,
	): ResolvedToken | null {
		switch (token) {
			case 'ME': {
				if (context.userId == null) {
					return null;
				}

				return {
					id: String(context.userId),
					label: context.userDisplayName ?? `User ${context.userId}`,
				};
			}

			case 'CURRENT_CUSTOMER': {
				if (context.customerId == null) {
					return null;
				}

				return {
					id: context.customerId,
					label: 'Current Customer',
				};
			}

			case 'MY_WAREHOUSE': {
				// PD-13742: resolve to the user's active warehouse so the list chip
				// shows the real warehouse name. No active warehouse (no default set)
				// → null, which drops the token and the list falls back to all the
				// warehouses the user can see.
				if (context.activeWarehouse == null) {
					return null;
				}

				return {
					id: String(context.activeWarehouse.id),
					label: context.activeWarehouse.name,
				};
			}

			case 'TODAY': {
				const date = context.referenceDate ?? new Date();
				const dateStr = date.toISOString().split('T')[0];

				return {
					id: dateStr ?? '',
					label: 'Today',
				};
			}

			case 'NOW': {
				const date = context.referenceDate ?? new Date();

				return {
					id: date.toISOString(),
					label: 'Now',
				};
			}

			default:
				return null;
		}
	}

	/**
	 * Check if a value is a token that needs resolution.
	 */
	static isToken(value: unknown): value is TokenType {
		if (typeof value !== 'string') {
			return false;
		}

		const tokens: Array<string> = [
			'ME',
			'CURRENT_CUSTOMER',
			'MY_WAREHOUSE',
			'TODAY',
			'NOW',
		];

		return tokens.includes(value);
	}

	/**
	 * Resolve all token values in a filter state object.
	 *
	 * @param filterState The filter state with potential token values
	 * @param context The context for token resolution
	 * @param options Tokens to keep unresolved (see ResolveFiltersOptionsModel)
	 * @returns New filter state with tokens resolved
	 */
	static resolveFilters(
		filterState: Record<string, Array<{ id: unknown; label?: string }>>,
		context: TokenContext,
		options: ResolveFiltersOptionsModel = {},
	): Record<string, Array<{ id: unknown; label?: string }>> {
		const resolvedFilters: Record<
			string,
			Array<{ id: unknown; label?: string }>
		> = {};
		const preservedTokens = new Set<unknown>(options.preserveTokens ?? []);

		for (const [filterType, values] of Object.entries(filterState)) {
			if (!Array.isArray(values)) {
				resolvedFilters[filterType] = values;

				continue;
			}

			// Use a Set to track unique IDs and prevent duplicates
			const seenIds = new Set<string>();
			const uniqueValues: Array<{ id: unknown; label?: string }> = [];

			for (const value of values) {
				const valueId = value.id;

				// Handle tokens (ME, CURRENT_CUSTOMER, etc.); preserved tokens fall
				// through and are kept like a regular value.
				if (this.isToken(valueId) && !preservedTokens.has(valueId)) {
					const resolved = this.resolveToken(valueId, context);

					if (resolved != null && !seenIds.has(resolved.id)) {
						seenIds.add(resolved.id);
						uniqueValues.push(resolved);
					}

					continue;
				}

				// Regular value - keep as-is but dedupe
				const idStr = String(valueId);

				if (!seenIds.has(idStr)) {
					seenIds.add(idStr);
					uniqueValues.push(value);
				}
			}

			resolvedFilters[filterType] = uniqueValues;
		}

		return resolvedFilters;
	}

	/**
	 * Replace only the ME token with the current user, for an outgoing request
	 * (PD-15272). List filter state keeps ME as a token so a saved or shared
	 * list follows whoever is viewing it; requests still send the viewer's id
	 * because not every list endpoint resolves the token server-side.
	 *
	 * Every other value — other tokens included — is returned untouched, and a
	 * filter with no ME value keeps its array unchanged. The resolved user and
	 * an explicit pick of the same user collapse into one value. Without a user
	 * id the state is returned as-is (the server resolves ME where it can).
	 *
	 * @param filterState The filter state about to be sent
	 * @param context The context for token resolution
	 * @returns Filter state with ME replaced by the current user
	 */
	public static resolveMeTokens<TValue extends { id: unknown; label?: string }>(
		filterState: Record<string, Array<TValue>>,
		context: TokenContext,
	): Record<string, Array<TValue | ResolvedToken>> {
		const me = this.resolveToken('ME', context);

		if (me == null) {
			return filterState;
		}

		const resolved: Record<string, Array<TValue | ResolvedToken>> = {};

		for (const [filterType, values] of Object.entries(filterState)) {
			// Runtime shapes are looser than the type (date ranges arrive as bare
			// objects), so anything that is not an array passes straight through.
			if (!Array.isArray(values) || !values.some((v) => this._isMe(v))) {
				resolved[filterType] = values;

				continue;
			}

			const seenIds = new Set<string>();
			const next: Array<TValue | ResolvedToken> = [];

			for (const value of values) {
				const entry: TValue | ResolvedToken = this._isMe(value) ? me : value;
				const idKey = this._idKey(entry);

				if (!seenIds.has(idKey)) {
					seenIds.add(idKey);
					next.push(entry);
				}
			}

			resolved[filterType] = next;
		}

		return resolved;
	}

	/** De-duplication key for a filter value: its id, or the raw value itself. */
	private static _idKey(value: unknown): string {
		if (value != null && typeof value === 'object' && 'id' in value) {
			return String((value as { id: unknown }).id);
		}

		return String(value);
	}

	/** Whether a filter value is the ME token option. */
	private static _isMe(value: unknown): boolean {
		return (
			value != null &&
			typeof value === 'object' &&
			(value as { id?: unknown }).id === 'ME'
		);
	}

	/**
	 * Create a TokenContext from AuthService state.
	 * This is a convenience method for creating context from auth state.
	 *
	 * @param authState The auth state from AuthService
	 * @param additionalContext Additional context like customerId
	 */
	static createContextFromAuth(
		authState: {
			user?: {
				id?: number;
				instanceUserId?: number;
				firstName?: string;
				lastName?: string;
			} | null;
		},
		additionalContext?: Partial<TokenContext>,
	): TokenContext {
		const user = authState.user;

		let userDisplayName = '';

		if (user != null) {
			const firstName = user.firstName ?? '';
			const lastName = user.lastName ?? '';
			userDisplayName = `${firstName} ${lastName}`.trim();

			if (userDisplayName === '') {
				userDisplayName =
					user.instanceUserId != null
						? `User ${user.instanceUserId}`
						: user.id != null
							? `User ${user.id}`
							: '';
			}
		}

		return {
			userId: user?.instanceUserId,
			userDisplayName,
			...additionalContext,
		};
	}
}
